// FORMULÁRIO PÓS-LIGAÇÃO (wrap-up): a definição que a org publica e as respostas
// de cada chamada. Espelha a feature `call-form` do back — /v1/call-forms (a
// lista), /v1/call-form/:id (um, e o ciclo ativar/desativar/duplicar/excluir),
// /v1/call-form (o ATIVO, sem id) e POST /v1/calls/:id/form (as respostas).
//
// São duas leituras muito diferentes do mesmo objeto e por isso moram juntas: o
// GESTOR monta a definição no construtor e o VENDEDOR preenche o que foi
// publicado. O tipo `FormField` é a fronteira entre as duas — separar "definição"
// de "resposta" daria dois arquivos, um deles importando os cinco tipos do outro.
//
// Não é tabulação (./dispositions.js): o desfecho da ligação é uma escolha só,
// de um catálogo fechado; isto é um questionário livre da marca. As duas coisas
// convivem no mesmo wrap-up e nenhuma substitui a outra.
//
// A avaliação da IA (./ai-call-eval.js) também aparece no wrap-up, mas é outra
// feature do back, com campos FIXOS e só para ligação de IA: os dois módulos não
// compartilham tipo nem rota, e nenhum importa o outro.
import { api, toApiError } from "./client.js";

export const FORM_FIELD_TYPES = [
  "radio",
  "select",
  "checkbox",
  "rating",
  "number",
  "text",
  "textarea",
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FORM_FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  radio: "Escolha única (rádio)",
  select: "Lista suspensa",
  checkbox: "Múltipla escolha",
  rating: "Nota (escala)",
  number: "Número",
  text: "Texto curto",
  textarea: "Texto longo",
};

export interface FormFieldOption {
  value: string;
  label: string;
}

// Um campo da definição. Campos de config específicos por tipo são opcionais no
// tipo do front (o back valida o shape correto por tipo).
export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  order: number;
  active: boolean;
  options?: FormFieldOption[]; // radio/select/checkbox
  min?: number; // rating/number
  max?: number; // rating/number
  maxLength?: number; // text/textarea
}

// Definição de formulário da org (múltiplos formulários, um ativo por vez).
export interface CallFormDef {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  fields: FormField[];
  active: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// GET /v1/call-forms — lista da org (gestão).
export async function listCallForms(): Promise<{ forms: CallFormDef[] }> {
  const { data, error } = await api<{ forms: CallFormDef[] }>("/v1/call-forms");
  if (error) throw toApiError("GET /v1/call-forms", error);
  return data;
}

// GET /v1/call-form — form ATIVO da org (wrap-up). { form: null } se nenhum ativo.
export async function getActiveCallForm(): Promise<{ form: CallFormDef | null }> {
  const { data, error } = await api<{ form: CallFormDef | null }>("/v1/call-form");
  if (error) throw toApiError("GET /v1/call-form", error);
  return data;
}

// GET /v1/call-form/:id — um form para edição (gestão).
export async function getCallForm(id: string): Promise<{ form: CallFormDef | null }> {
  const { data, error } = await api<{ form: CallFormDef | null }>(`/v1/call-form/${id}`);
  if (error) throw toApiError("GET /v1/call-form/:id", error);
  return data;
}

export async function createCallForm(input: {
  name: string;
  description?: string | null;
  fields: FormField[];
}): Promise<CallFormDef> {
  const { data, error } = await api<CallFormDef>("/v1/call-forms", { method: "POST", body: input });
  if (error) throw toApiError("POST /v1/call-forms", error);
  return data;
}

export async function updateCallForm(
  id: string,
  input: {
    name: string;
    description?: string | null;
    fields: FormField[];
  },
): Promise<CallFormDef> {
  const { data, error } = await api<CallFormDef>(`/v1/call-form/${id}`, {
    method: "PUT",
    body: input,
  });
  if (error) throw toApiError("PUT /v1/call-form/:id", error);
  return data;
}

export async function activateCallForm(id: string): Promise<CallFormDef> {
  const { data, error } = await api<CallFormDef>(`/v1/call-form/${id}/activate`, {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/call-form/:id/activate", error);
  return data;
}

export async function deactivateCallForm(id: string): Promise<CallFormDef> {
  const { data, error } = await api<CallFormDef>(`/v1/call-form/${id}/deactivate`, {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/call-form/:id/deactivate", error);
  return data;
}

export async function duplicateCallForm(id: string): Promise<CallFormDef> {
  const { data, error } = await api<CallFormDef>(`/v1/call-form/${id}/duplicate`, {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/call-form/:id/duplicate", error);
  return data;
}

export async function deleteCallForm(id: string): Promise<void> {
  const { error } = await api(`/v1/call-form/${id}`, { method: "DELETE" });
  if (error) throw toApiError("DELETE /v1/call-form/:id", error);
}

// POST /v1/calls/:id/form — grava as respostas de uma chamada (usado no wrap-up,
// inc 4). Valida contra a definição publicada no back.
export async function submitCallForm(
  callId: string,
  answers: Record<string, unknown>,
): Promise<{ submitted: true; formVersion: number }> {
  const { data, error } = await api<{ submitted: true; formVersion: number }>(
    `/v1/calls/${callId}/form`,
    { method: "POST", body: { answers } },
  );
  if (error) throw toApiError(`POST /v1/calls/${callId}/form`, error);
  return data;
}
